import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import FileBrowser from "@/components/FileBrowser";
import { getSkill, getSkillFiles } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useSkillPath } from "@/lib/skillPath";
import { useSeoMeta } from "@/lib/useSeoMeta";
import type { SkillDetail, SkillFile } from "@/lib/types";

/**
 * Full-page, GitHub-style file browser for a skill: file tree on the left, the selected file's
 * content on the right. The current file lives in the URL (`/skill/:slug/<path>`) so it's
 * shareable and the back button walks between files. Replaces the old "View all" modal.
 */
export default function SkillFilesPage() {
  const params = useParams();
  const slug = String(params.skillSlug);
  const filePath = params["*"] || undefined; // the splat = the file path being viewed
  const navigate = useNavigate();
  const skillPath = useSkillPath();
  const { accessToken } = useAuth();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  // Canonical points back at the skill's main page — this file browser is a view into the
  // same content, not a distinct page worth its own search result.
  useSeoMeta({
    title: skill ? `${skill.name} — SkillPatch` : "SkillPatch",
    description: skill?.summary,
    path: `/skill/${slug}`,
  });
  const [files, setFiles] = useState<SkillFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSkill(slug, accessToken)
      .then((s) => { if (!cancelled) setSkill(s); })
      .catch(() => { if (!cancelled) setError("Not found"); });
    getSkillFiles(slug, accessToken)
      .then((f) => { if (!cancelled) setFiles(f); })
      .catch(() => { if (!cancelled) setFiles([]); });
    return () => { cancelled = true; };
  }, [slug, accessToken]);

  if (error) return <main className="mx-auto max-w-3xl px-6 py-16 text-text/60">{error}</main>;

  return (
    // Fixed-height app view: header (4.5rem) + the slim legal-strip footer (~3rem) = 7.5rem, each
    // pane scrolling on its own — keep in sync with SiteChrome's min-h. Use `dvh` (dynamic viewport)
    // not `vh` so the pane fits under the mobile browser's address bar instead of running past it.
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-[560px] flex-col">
      {/* Breadcrumb: back to the skill, then the file currently being viewed */}
      <div className="flex items-center gap-2 px-5 py-3 text-sm">
        <Link to={skillPath(slug)} className="flex items-center gap-1.5 text-text/60 transition hover:text-text">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span className="font-medium first-letter:uppercase">{skill?.name ?? slug}</span>
        </Link>
        {filePath && (
          <>
            <span className="text-text/30">/</span>
            <span className="truncate font-mono text-xs text-text/55">{filePath}</span>
          </>
        )}
      </div>

      {files === null ? (
        <div className="grid flex-1 place-items-center text-sm text-text/40">Loading…</div>
      ) : (
        <FileBrowser
          files={files}
          initialPath={filePath}
          onSelect={(p) => navigate(skillPath(slug, `/${p}`))}
        />
      )}
    </div>
  );
}
